import React, { useState, useContext, useEffect } from "react";
import UserCoursesModal from "./UserCoursesModal";
import UserListStatusModal from "./UserListStatusModal";
import NotificationContext from "../context/NotificationContext";
import { useMutationLock } from "../hooks/useMutationLock";
import { USER_UPLOAD_SAMPLE } from "../utils/csvTemplates";
import BulkCsvControls from "./BulkCsvControls";
import PaginationFooter from "./PaginationFooter";
import AppTooltip from "./AppTooltip";
import * as registrationsApi from "../api/registrations";
import { BULK_UPLOAD_ENDPOINT as USERS_BULK_UPLOAD_ENDPOINT } from "../api/users";

const LIST_RAIL = {
  black: "#1f1b2e",
  grey: "#8a8f9c",
  none: "transparent",
};

const LIST_RAIL_WIDTH = 10;

/* App pastels for list actions (rails stay literal grey/black). */
const LIST_BTN = {
  grey: { backgroundColor: "#f2e6c9", color: "#654343" },
  black: { backgroundColor: "#f2c4c4", color: "#654343" },
  reason: { backgroundColor: "#d5d1fd", color: "#403b55" },
  clear: { backgroundColor: "#bff1db", color: "#355c4a" },
  remove: { backgroundColor: "#f9e1be", color: "#654343" },
};

const listActionBtn = {
  border: "none",
  borderRadius: "7px",
  fontWeight: "600",
  fontSize: "11px",
  padding: "6px 10px",
  whiteSpace: "nowrap",
  lineHeight: 1.2,
};

export function AdminTable({
  data,
  columnKeys,
  handleEdit,
  handleSave,
  handleDelete,
  canEdit,
  onRefresh,
  onUpdateListStatus,
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  searchEmail = "",
  onSearchEmailChange,
  loading = false,
}) {
  const notificationCtx = useContext(NotificationContext);
  const { busy, run } = useMutationLock();

  // Edit states for each cell
  const [editStates, setEditStates] = useState({});

  // Auto-resize textarea that should always display its full content
  const autoResizeTextarea = (textarea) => {
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useEffect(() => {
    const textareas = document.querySelectorAll(
      "textarea[data-autoresize]",
    );
    textareas.forEach(autoResizeTextarea);
  }, [data]);

  // Local edit logic
  const handleInputChange = (index, key, value) => {
    if (!canEdit) return;
    handleEdit(index, key, value);
    setEditStates((prev) => ({
      ...prev,
      [`${index}-${key}`]: true,
    }));
  };

  const handleSaveClick = async (userId, key, value, index) => {
    if (!canEdit) return;
    await run(async () => {
      await handleSave(userId, key, value);
      setEditStates((prev) => ({
        ...prev,
        [`${index}-${key}`]: false,
      }));
    });
  };

  const [usercourses, setusercourses] = useState([]);
  const [showusermodal, setshowusermodal] = useState(false);
  const [detailStudent, setDetailStudent] = useState(null);
  const [listModal, setListModal] = useState(null);

  const showUserDetail = async (item) => {
    try {
      const response = await registrationsApi.myCourses({
        user: item.email,
      });
      if (response.data) {
        setDetailStudent(item);
        setusercourses(response.data);
        setshowusermodal(true);
      }
    } catch (error) {
      console.error(
        "Error:",
        error.response ? error.response.data : error.message,
      );
    }
  };

  const handleListStatusSubmit = async (payload) => {
    if (!listModal?.user?.id || !onUpdateListStatus) return;
    await onUpdateListStatus(listModal.user.id, payload);
  };

  const handleUnmarkListStatus = (item) => {
    if (!canEdit || !onUpdateListStatus) return;

    const confirmUnmark = async () => {
      try {
        await onUpdateListStatus(item.id, {
          blackListed: false,
          greyListed: false,
        });
        notificationCtx.show({
          message: "List status cleared.",
          stat: "success",
        });
      } catch (error) {
        notificationCtx.show({
          message:
            error?.response?.data?.message || "Failed to clear list status.",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: `Remove list status for ${item.name || item.email || "this student"}?`,
      stat: "info",
      onConfirm: confirmUnmark,
    });
  };

  const th_style = {
    minWidth: "140px",
    whiteSpace: "nowrap",
    backgroundColor: "#d5d1fd",
    color: "#302d45",
    fontWeight: "700",
    padding: "14px 16px",
    textAlign: "center",
    letterSpacing: "0.3px",
    fontSize: "13px",
    borderBottom: "1px solid #c8c2ed",
  };

  const td_style = {
    padding: "12px 14px",
    minWidth: "150px",
    maxWidth: "280px",
    verticalAlign: "top",
    borderBottom: "1px solid #f0eef5",
    color: "#403b55",
    fontSize: "13px",
    lineHeight: "1.5",
    overflow: "visible",
  };

  return (
    <div className="d-flex flex-column" style={{ height: "100%" }}>
      {/* ================= TOP CONTROLS ================= */}

      <div
        style={{
          backgroundColor: "#d5d1fd",
          borderRadius: "20px",
          padding: "24px 28px",
          flexShrink: 0,
          boxShadow: "0 2px 8px rgba(48,45,69,0.04)",
        }}
      >
        {/* Header */}
        <div
          className="d-flex justify-content-between align-items-center"
          style={{
            gap: "30px",
          }}
        >
          {/* Title */}
          <div>
            <h1
              className="mb-1"
              style={{
                // fontSize: "28px",
                fontWeight: "700",
                color: "#302d45",
              }}
            >
              ALL STUDENTS
            </h1>
            <small
              style={{
                color: "#625d75",
              }}
            >
              Manage users and their account details
            </small>
          </div>

          {/* Upload Actions */}
          <BulkCsvControls
            canEdit={canEdit}
            sampleRows={USER_UPLOAD_SAMPLE}
            templateFilename="users_upload_template.csv"
            endpoint={USERS_BULK_UPLOAD_ENDPOINT}
            notificationCtx={notificationCtx}
            onSuccess={onRefresh}
          />
        </div>

        {/* Search */}
        <div
          style={{
            marginTop: "20px",
            paddingTop: "18px",
            borderTop: "1px solid rgba(80,70,110,0.12)",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: "700",
              color: "#403b55",
              marginBottom: "6px",
            }}
          >
            SEARCH STUDENTS
          </div>

          <div
            style={{
              position: "relative",
              maxWidth: "420px",
            }}
          >
            <i
              className="fa-solid fa-magnifying-glass"
              style={{
                position: "absolute",
                left: "13px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#8a849b",
                fontSize: "13px",
                pointerEvents: "none",
              }}
            />

            <input
              type="text"
              placeholder="Search by email..."
              value={searchEmail}
              onChange={(e) => onSearchEmailChange?.(e.target.value)}
              className="form-control"
              style={{
                height: "40px",
                border: "none",
                borderRadius: "9px",
                padding: "8px 13px 8px 36px",
                backgroundColor: "#fff",
                boxShadow: "0 2px 6px rgba(48,45,69,0.05)",
                fontSize: "13px",
              }}
            />
          </div>
        </div>
      </div>

      {/* ================= TABLE AREA ================= */}

      {data.length === 0 ? (
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{
            flex: 1,
            color: "#77718b",
          }}
        >
          <div
            style={{
              width: "52px",
              height: "52px",
              borderRadius: "50%",
              backgroundColor: "#f3f1fb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "12px",
            }}
          >
            <i
              className="fa-solid fa-users"
              style={{
                color: "#8a83b4",
                fontSize: "20px",
              }}
            />
          </div>

          <div
            style={{
              fontSize: "17px",
              fontWeight: "600",
              color: "#403b55",
            }}
          >
            No students available
          </div>

          <small
            style={{
              marginTop: "4px",
              color: "#8a8497",
            }}
          >
            There are currently no students to display.
          </small>
        </div>
      ) : (
        <>
          {/* ================= SPACING ================= */}
          <div style={{ height: "16px", flexShrink: 0 }} />

          {/* Table */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              borderRadius: "16px",
              backgroundColor: "#fff",
              boxShadow: "0 3px 12px rgba(48, 45, 69, 0.08)",
              border: "1px solid #eeeaf8",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              <table
                border="0"
                cellPadding="10"
                cellSpacing="0"
                style={{
                  minWidth: "1100px",
                  width: "100%",
                  margin: "0",
                  tableLayout: "auto",
                  background: "#ffffff",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <thead>
                  <tr>
                    {columnKeys.map((key) => (
                      <th
                        key={key}
                        style={{
                          ...th_style,
                          ...(key === "email"
                            ? { minWidth: "200px" }
                            : null),
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                        }}
                      >
                        {key.toUpperCase()}
                      </th>
                    ))}

                    {canEdit && (
                      <th
                        style={{
                          ...th_style,
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                        }}
                      >
                        ACTIONS
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {data.map((item, index) => {
                    const isBlackListed = Boolean(item.blackListed);
                    const isGreyListed = Boolean(item.greyListed);
                    const railColor = isBlackListed
                      ? LIST_RAIL.black
                      : isGreyListed
                        ? LIST_RAIL.grey
                        : LIST_RAIL.none;
                    return (
                    <tr
                      key={item.id ?? index}
                      style={{
                        transition: "background-color 0.15s ease",
                        boxShadow: `inset ${LIST_RAIL_WIDTH}px 0 0 0 ${railColor}`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#faf9fd";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff";
                      }}
                    >
                      {columnKeys.map((key) => (
                        <td
                          key={key}
                          style={{
                            ...td_style,
                            ...(key === "email"
                              ? { minWidth: "200px", maxWidth: "260px" }
                              : null),
                          }}
                        >
                          {key === "id" || key === "email" ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "8px",
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  whiteSpace: "normal",
                                  overflowWrap: "break-word",
                                  wordBreak: "normal",
                                  lineHeight: "1.5",
                                }}
                              >
                                {key === "email" && item[key]
                                  ? (() => {
                                      const email = String(item[key]);
                                      const at = email.indexOf("@");
                                      if (at <= 0 || at === email.length - 1) {
                                        return email;
                                      }
                                      return (
                                        <>
                                          {email.slice(0, at + 1)}
                                          <wbr />
                                          {email.slice(at + 1)}
                                        </>
                                      );
                                    })()
                                  : item[key]}
                              </div>
                              <AppTooltip label="View student details">
                              <span
                                onClick={() => showUserDetail(item)}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "7px",
                                  backgroundColor: "#d5d1fd",
                                  color: "#403b55",
                                  textDecoration: "none",
                                  transition: "all 0.2s ease",
                                  cursor: "pointer",
                                }}
                              >
                                <i
                                  className="fa-solid fa-arrow-up-right-from-square"
                                  style={{
                                    fontSize: "11px",
                                  }}
                                />
                              </span>
                              </AppTooltip>
                            </div>
                          ) : key.toLowerCase().includes("picture") ||
                            key.toLowerCase().includes("image") ||
                            key.toLowerCase().includes("bank") ? (
                            item[key] &&
                            String(item[key]).startsWith("https://") ? (
                              <a
                                href={item[key]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn"
                                style={{
                                  backgroundColor: "#d5d1fd",
                                  border: "none",
                                  color: "#403b55",
                                  fontWeight: "600",
                                  fontSize: "12px",
                                  padding: "6px 14px",
                                  borderRadius: "7px",
                                  textDecoration: "none",
                                }}
                              >
                                <i
                                  className="fa-regular fa-eye"
                                  style={{
                                    marginRight: "6px",
                                  }}
                                />
                                View
                              </a>
                            ) : (
                              <span style={{ color: "#888", fontSize: "12px" }}>
                                —
                              </span>
                            )
                          ) : (
                            <div
                              style={{
                                textAlign: "center",
                              }}
                            >
                              <textarea
                                value={item[key] ?? ""}
                                readOnly={!canEdit}
                                onChange={(e) =>
                                  handleInputChange(index, key, e.target.value)
                                }
                                onInput={(e) => autoResizeTextarea(e.target)}
                                ref={(node) => autoResizeTextarea(node)}
                                rows={1}
                                data-autoresize
                                className="text-center"
                                style={{
                                  display: "block",
                                  width: "100%",
                                  minHeight: "32px",
                                  border: "1px solid transparent",
                                  outline: "none",
                                  backgroundColor: "transparent",
                                  resize: "none",
                                  overflow: "hidden",
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                  borderRadius: "6px",
                                  padding: "6px 8px",
                                  color: "#403b55",
                                  fontSize: "13px",
                                  lineHeight: "1.4",
                                  boxSizing: "border-box",
                                  cursor: canEdit ? "text" : "default",
                                }}
                              />

                              {canEdit && editStates[`${index}-${key}`] && (
                                <button
                                  onClick={() =>
                                    handleSaveClick(
                                      item.id,
                                      key,
                                      item[key],
                                      index,
                                    )
                                  }
                                  disabled={busy}
                                  className="btn"
                                  style={{
                                    backgroundColor: "#bff1db",
                                    border: "none",
                                    color: "#355c4a",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    padding: "5px 12px",
                                    borderRadius: "6px",
                                    marginTop: "5px",
                                  }}
                                >
                                  <i
                                    className="fa-solid fa-check"
                                    style={{
                                      marginRight: "5px",
                                    }}
                                  />
                                  Save
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      ))}

                      {canEdit && (
                        <td
                          style={{
                            ...td_style,
                            textAlign: "center",
                            minWidth: "200px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "stretch",
                              gap: "6px",
                              minWidth: "150px",
                            }}
                          >
                            {!isBlackListed && !isGreyListed && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "6px",
                                }}
                              >
                                <button
                                  type="button"
                                  className="btn"
                                  disabled={busy}
                                  onClick={() =>
                                    setListModal({ mode: "grey", user: item })
                                  }
                                  style={{
                                    ...listActionBtn,
                                    flex: 1,
                                    ...LIST_BTN.grey,
                                  }}
                                >
                                  Grey list
                                </button>
                                <button
                                  type="button"
                                  className="btn"
                                  disabled={busy}
                                  onClick={() =>
                                    setListModal({
                                      mode: "black",
                                      user: item,
                                    })
                                  }
                                  style={{
                                    ...listActionBtn,
                                    flex: 1,
                                    ...LIST_BTN.black,
                                  }}
                                >
                                  Black list
                                </button>
                              </div>
                            )}

                            {(isGreyListed || isBlackListed) && (
                              <>
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() =>
                                    setListModal({ mode: "view", user: item })
                                  }
                                  style={{
                                    ...listActionBtn,
                                    ...LIST_BTN.reason,
                                    width: "100%",
                                    height: "48px",
                                    boxSizing: "border-box",
                                    padding: "7px 10px",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    gap: "3px",
                                    textAlign: "left",
                                    overflow: "hidden",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      fontWeight: 700,
                                      letterSpacing: "0.04em",
                                      textTransform: "uppercase",
                                      color: "#403b55",
                                      lineHeight: 1,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {isBlackListed
                                      ? "Black listed"
                                      : "Grey listed"}
                                  </span>
                                  <span
                                    style={{
                                      display: "block",
                                      width: "100%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      fontWeight: 500,
                                      opacity: 0.9,
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    {item.listReason?.trim()
                                      ? item.listReason.trim()
                                      : "No reason"}
                                  </span>
                                </button>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                  }}
                                >
                                  <button
                                    type="button"
                                    className="btn"
                                    disabled={busy}
                                    onClick={() =>
                                      setListModal({
                                        mode: isGreyListed ? "black" : "grey",
                                        user: item,
                                      })
                                    }
                                    style={{
                                      ...listActionBtn,
                                      flex: 1,
                                      ...(isGreyListed
                                        ? LIST_BTN.black
                                        : LIST_BTN.grey),
                                    }}
                                  >
                                    {isGreyListed ? "To black" : "To grey"}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn"
                                    disabled={busy}
                                    onClick={() =>
                                      handleUnmarkListStatus(item)
                                    }
                                    style={{
                                      ...listActionBtn,
                                      flex: 1,
                                      ...LIST_BTN.clear,
                                    }}
                                  >
                                    Clear
                                  </button>
                                </div>
                              </>
                            )}

                            <button
                              type="button"
                              className="btn"
                              onClick={() => handleDelete(item.id)}
                              style={{
                                ...listActionBtn,
                                width: "100%",
                                ...LIST_BTN.remove,
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ================= SPACING ================= */}
          <div style={{ height: "16px", flexShrink: 0 }} />

          {/* ================= FOOTER ================= */}
          <PaginationFooter
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={onPageChange}
            itemLabel="students"
            disabled={loading}
          />

          {showusermodal && (
            <UserCoursesModal
              usercourses={usercourses}
              student={detailStudent}
              onClose={() => {
                setshowusermodal(false);
                setDetailStudent(null);
              }}
              canEdit={canEdit}
              onRefresh={onRefresh}
            />
          )}

          {listModal && (
            <UserListStatusModal
              mode={listModal.mode}
              user={listModal.user}
              onClose={() => setListModal(null)}
              onSubmit={handleListStatusSubmit}
            />
          )}
        </>
      )}
    </div>
  );
}
